import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

// Importar logo para o PDF
import logoImage from '../../assets/logoCartAzul.png';

// Configurar dayjs para português
dayjs.locale('pt-br');

/**
 * Classe responsável pela geração de PDF do Relatório de Senhas Detalhadas
 * Usa método simplificado similar ao ModalSenhasUsuario
 */
class PDFSenhasDetalhadas {
  /**
   * Gera PDF do relatório de senhas detalhadas
   * 
   * @param {Object} options Opções para geração do PDF
   * @param {Array} options.senhas Array de senhas para o relatório
   * @param {Object} options.filtros Filtros aplicados no relatório
   * @param {Function} options.onSuccess Callback para sucesso
   * @param {Function} options.onError Callback para erro
   */
  gerarPDF(options) {
    const {
      senhas = [],
      filtros = {},
      onSuccess,
      onError
    } = options;

    try {
      console.log('Gerando PDF de Senhas Detalhadas, aguarde...');
      
      // Validar dados
      if (!senhas || senhas.length === 0) {
        throw new Error('Nenhuma senha disponível para gerar o relatório');
      }
      
      // Processar dados para o PDF
      const dadosProcessados = this.processarDadosSenhas(senhas);
      
      // Gerar PDF com método simplificado
      this.gerarPDFSimplificado({
        senhas,
        dadosProcessados,
        filtros,
        onSuccess,
        onError
      });
      
    } catch (error) {
      console.error('Erro ao gerar PDF de Senhas Detalhadas:', error);
      if (typeof onError === 'function') {
        onError('Ocorreu um erro ao gerar o PDF: ' + error.message);
      }
    }
  };

  /**
   * Processa os dados das senhas para estatísticas do PDF
   */
  processarDadosSenhas(senhas) {
    const totalSenhas = senhas.length;
    
    // Contar por status
    const statusCount = {};
    senhas.forEach(senha => {
      const status = senha.status || 'N/A';
      statusCount[status] = (statusCount[status] || 0) + 1;
    });
    
    // Contar por serviço (usando servico_nome que é o campo correto)
    const servicoCount = {};
    senhas.forEach(senha => {
      const servico = senha.servico_nome || 'N/A';
      servicoCount[servico] = (servicoCount[servico] || 0) + 1;
    });
    
    // Contar por tipo (usando preferencial que é boolean)
    const tipoCount = {};
    senhas.forEach(senha => {
      const tipo = senha.preferencial ? 'Preferencial' : 'Comum';
      tipoCount[tipo] = (tipoCount[tipo] || 0) + 1;
    });
    
    return {
      totalSenhas,
      statusCount,
      servicoCount,
      tipoCount
    };
  }

  /**
   * Traduz status para português
   */
  traduzirStatus(status) {
    const traducoes = {
      'AGUARDANDO': 'Aguardando',
      'CHAMADA': 'Chamada',
      'EM_ATENDIMENTO': 'Em Atendimento',
      'FINALIZADA': 'Finalizada',
      'CANCELADA': 'Cancelada',
      'NAO_COMPARECEU': 'Não Compareceu',
      'REABERTA': 'Reaberta'
    };
    return traducoes[status] || status;
  }

  /**
   * Formatar data/hora para exibição
   */
  formatarDataHora(dataHora) {
    if (!dataHora) return '-';
    return dayjs(dataHora).format('DD/MM/YYYY HH:mm:ss');
  }

  /**
   * Calcular tempo de atendimento
   */
  calcularTempoAtendimento(inicioAtendimento, fimAtendimento) {
    if (!inicioAtendimento || !fimAtendimento) return '-';
    
    const inicio = dayjs(inicioAtendimento);
    const fim = dayjs(fimAtendimento);
    const duracaoSegundos = fim.diff(inicio, 'second');
    
    if (duracaoSegundos < 60) {
      return `${duracaoSegundos}seg`;
    } else {
      const minutos = Math.floor(duracaoSegundos / 60);
      const segundos = duracaoSegundos % 60;
      
      if (minutos < 60) {
        return `${minutos}min ${segundos}seg`;
      } else {
        const horas = Math.floor(minutos / 60);
        const minutosRestantes = minutos % 60;
        return `${horas}h ${minutosRestantes}min ${segundos}seg`;
      }
    }
  }

  /**
   * Obter cor do status
   */
  getStatusColor(status) {
    const cores = {
      'AGUARDANDO': '#2196f3',
      'CHAMADA': '#ff9800', 
      'EM_ATENDIMENTO': '#9c27b0',
      'FINALIZADA': '#4caf50',
      'CANCELADA': '#f44336',
      'NAO_COMPARECEU': '#795548',
      'REABERTA': '#607d8b'
    };
    return cores[status] || '#9e9e9e';
  }

  /**
   * Gera PDF usando método simplificado (window.print)
   */
  gerarPDFSimplificado(options) {
    const {
      senhas,
      dadosProcessados,
      filtros,
      onSuccess,
      onError
    } = options;

    try {
      const printWindow = window.open('', '_blank');
      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>Relatório Detalhado de Senhas - ${dayjs().format('DD/MM/YYYY')}</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              
              body {
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                line-height: 1.6;
                color: #333;
                background: white;
                padding: 20px;
              }
              
              .header {
                text-align: center;
                margin-bottom: 30px;
                border-bottom: 3px solid #1976d2;
                padding-bottom: 20px;
                position: relative;
              }
              
              .header-logo {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 20px;
                margin-bottom: 15px;
              }
              
              .header-logo img {
                height: 40px;
                width: auto;
              }
              
              .header h1 {
                color: #1976d2;
                font-size: 28px;
                margin-bottom: 10px;
              }
              
              .header p {
                color: #666;
                font-size: 14px;
              }
              
              .periodo-info {
                background: #f8f9fa;
                border-radius: 8px;
                padding: 15px;
                margin-bottom: 30px;
                border-left: 4px solid #1976d2;
                display: flex;
                justify-content: space-between;
                align-items: center;
              }
              
              .periodo-info h3 {
                color: #1976d2;
                margin-bottom: 5px;
              }
              
              .periodo-info p {
                color: #666;
                font-size: 14px;
              }
              
              .metrics-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 20px;
                margin-bottom: 30px;
              }
              
              .metric-card {
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                padding: 20px;
                text-align: center;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                position: relative;
                overflow: hidden;
              }
              
              .metric-card::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 4px;
                background: linear-gradient(90deg, #1976d2, #42a5f5);
              }
              
              .metric-card h3 {
                font-size: 32px;
                margin-bottom: 5px;
                font-weight: bold;
                color: #1976d2;
              }
              
              .metric-card p {
                color: #666;
                font-size: 14px;
                font-weight: 500;
              }
              
              .section {
                margin-bottom: 40px;
              }
              
              .section-title {
                color: #1976d2;
                font-size: 20px;
                margin-bottom: 15px;
                padding-bottom: 10px;
                border-bottom: 2px solid #e0e0e0;
                display: flex;
                align-items: center;
                gap: 10px;
              }
              
              .section-title::before {
                content: '';
                width: 4px;
                height: 20px;
                background: #1976d2;
                border-radius: 2px;
              }
              
              .table-container {
                overflow-x: auto;
                margin-bottom: 20px;
                border-radius: 8px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
              
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 11px;
                background: white;
              }
              
              th, td {
                border: 1px solid #ddd;
                padding: 8px 6px;
                text-align: left;
              }
              
              th {
                background: #1976d2;
                color: white;
                font-weight: bold;
                text-align: center;
                font-size: 10px;
              }
              
              tr:nth-child(even) {
                background: #f8f9fa;
              }
              
              tr:hover {
                background: #e3f2fd;
              }
              
              .text-center { text-align: center; }
              .text-right { text-align: right; }
              
              .status-chip {
                padding: 4px 8px;
                border-radius: 12px;
                font-size: 9px;
                font-weight: bold;
                color: white;
                display: inline-block;
              }
              
              .status-aguardando { background-color: #2196f3; }
              .status-chamada { background-color: #ff9800; }
              .status-em_atendimento { background-color: #9c27b0; }
              .status-finalizada { background-color: #4caf50; }
              .status-cancelada { background-color: #f44336; }
              .status-nao_compareceu { background-color: #795548; }
              .status-reaberta { background-color: #607d8b; }
              
              .tipo-chip {
                padding: 2px 6px;
                border-radius: 8px;
                font-size: 9px;
                font-weight: bold;
                display: inline-block;
              }
              
              .tipo-comum {
                background-color: #e3f2fd;
                color: #1976d2;
              }
              
              .tipo-preferencial {
                background-color: #fff3e0;
                color: #f57c00;
              }
              
              .highlight {
                background: #fff3cd;
                border: 1px solid #ffeaa7;
                padding: 15px;
                border-radius: 8px;
                margin: 20px 0;
              }
              
              .highlight h4 {
                color: #856404;
                margin-bottom: 10px;
              }
              
              .highlight p {
                color: #856404;
                margin: 5px 0;
              }
              
              .footer {
                margin-top: 40px;
                text-align: center;
                color: #666;
                font-size: 12px;
                border-top: 1px solid #ddd;
                padding-top: 20px;
              }
              
              .footer img {
                height: 20px;
                width: auto;
                margin-bottom: 10px;
              }
              
              .stats-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                gap: 20px;
                margin-bottom: 30px;
              }
              
              .stats-card {
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                padding: 15px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
              
              .stats-card h4 {
                color: #1976d2;
                margin-bottom: 10px;
                font-size: 16px;
              }
              
              .stats-item {
                display: flex;
                justify-content: space-between;
                margin-bottom: 5px;
                font-size: 12px;
              }
              
              @media print {
                body {
                  padding: 0;
                }
                
                .metrics-grid {
                  grid-template-columns: repeat(4, 1fr);
                  gap: 15px;
                }
                
                .metric-card {
                  padding: 15px;
                }
                
                .metric-card h3 {
                  font-size: 24px;
                }
                
                table {
                  font-size: 9px;
                }
                
                th, td {
                  padding: 6px 4px;
                }
                
                .stats-grid {
                  grid-template-columns: repeat(2, 1fr);
                }
              }
              
              @page {
                margin: 1.5cm;
                size: A4;
              }
            </style>
          </head>
          <body>
            <!-- CABEÇALHO -->
            <div class="header">
              <div class="header-logo">
                <img src="${logoImage}" alt="Logo" />
                <div>
                  <h1>RELATÓRIO DETALHADO DE SENHAS</h1>
                  <p>Sistema de Gerenciamento de Senhas</p>
                </div>
              </div>
            </div>
            
            <!-- INFORMAÇÕES DO PERÍODO -->
            <div class="periodo-info">
              <div>
                <h3>Período do Relatório</h3>
                <p><strong>${filtros.dataInicio || 'N/A'}</strong> até <strong>${filtros.dataFim || 'N/A'}</strong></p>
              </div>
              <div>
                <h3>Gerado em</h3>
                <p>${dayjs().format('DD/MM/YYYY HH:mm')}</p>
              </div>
            </div>
            
            <!-- MÉTRICAS PRINCIPAIS -->
            <div class="section">
              <h2 class="section-title">📊 Resumo Geral</h2>
              <div class="metrics-grid">
                <div class="metric-card">
                  <h3>${dadosProcessados.totalSenhas}</h3>
                  <p>Total de Senhas</p>
                </div>
                <div class="metric-card">
                  <h3>${dadosProcessados.statusCount['FINALIZADA'] || 0}</h3>
                  <p>Senhas Finalizadas</p>
                </div>
                <div class="metric-card">
                  <h3>${dadosProcessados.statusCount['CANCELADA'] || 0}</h3>
                  <p>Senhas Canceladas</p>
                </div>
                <div class="metric-card">
                  <h3>${dadosProcessados.tipoCount['Preferencial'] || 0}</h3>
                  <p>Atendimentos Preferenciais</p>
                </div>
              </div>
            </div>
            
            <!-- ESTATÍSTICAS POR CATEGORIA -->
            <div class="section">
              <h2 class="section-title">📈 Estatísticas por Categoria</h2>
              <div class="stats-grid">
                <div class="stats-card">
                  <h4>Por Status</h4>
                  ${Object.entries(dadosProcessados.statusCount).map(([status, count]) => `
                    <div class="stats-item">
                      <span>${this.traduzirStatus(status)}</span>
                      <span><strong>${count}</strong></span>
                    </div>
                  `).join('')}
                </div>
                
                <div class="stats-card">
                  <h4>Por Tipo</h4>
                  ${Object.entries(dadosProcessados.tipoCount).map(([tipo, count]) => `
                    <div class="stats-item">
                      <span>${tipo}</span>
                      <span><strong>${count}</strong></span>
                    </div>
                  `).join('')}
                </div>
                
                                 <div class="stats-card">
                   <h4>Top 5 Serviços</h4>
                   ${Object.entries(dadosProcessados.servicoCount)
                     .filter(([servico]) => servico !== 'N/A')
                     .sort(([,a], [,b]) => b - a)
                     .slice(0, 5)
                     .map(([servico, count]) => `
                       <div class="stats-item">
                         <span>${servico.length > 25 ? servico.substring(0, 25) + '...' : servico}</span>
                         <span><strong>${count}</strong></span>
                       </div>
                     `).join('')}
                   ${Object.keys(dadosProcessados.servicoCount).filter(s => s !== 'N/A').length === 0 ? 
                     '<div class="stats-item"><span>Nenhum serviço encontrado</span><span>-</span></div>' : ''}
                 </div>
              </div>
            </div>
            
            <!-- TABELA DETALHADA DAS SENHAS -->
            <div class="section">
              <h2 class="section-title">📋 Detalhamento das Senhas</h2>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Senha</th>
                      <th>Data/Hora Emissão</th>
                      <th>Data/Hora Chamada</th>
                      <th>Data/Hora Início</th>
                      <th>Data/Hora Finalização</th>
                      <th>Tempo Atendimento</th>
                      <th>Serviço</th>
                      <th>Tipo</th>
                      <th>Status</th>
                      <th>Atendente</th>
                    </tr>
                  </thead>
                  <tbody>
                                         ${senhas.slice(0, 100).map(senha => `
                       <tr>
                         <td class="text-center"><strong>${senha.numero || 'N/A'}</strong></td>
                         <td class="text-center">${this.formatarDataHora(senha.emitida_em)}</td>
                         <td class="text-center">${this.formatarDataHora(senha.chamada_em)}</td>
                         <td class="text-center">${this.formatarDataHora(senha.inicio_atendimento)}</td>
                         <td class="text-center">${this.formatarDataHora(senha.finalizada_em)}</td>
                         <td class="text-center">${this.calcularTempoAtendimento(senha.inicio_atendimento, senha.finalizada_em)}</td>
                         <td>${senha.servico_nome || 'N/A'}</td>
                         <td class="text-center">
                           <span class="tipo-chip tipo-${senha.preferencial ? 'preferencial' : 'comum'}">${senha.preferencial ? 'Preferencial' : 'Comum'}</span>
                         </td>
                         <td class="text-center">
                           <span class="status-chip status-${(senha.status || 'aguardando').toLowerCase()}">${this.traduzirStatus(senha.status)}</span>
                         </td>
                         <td>${senha.usuario_nome || 'N/A'}</td>
                       </tr>
                     `).join('')}
                  </tbody>
                </table>
              </div>
              
              ${senhas.length > 100 ? `
                <div class="highlight">
                  <h4>ℹ️ Informação</h4>
                  <p><strong>Exibindo apenas as primeiras 100 senhas.</strong> Total de ${senhas.length} senhas no período.</p>
                  <p>Para visualizar todas as senhas, use os filtros no sistema ou exporte para Excel.</p>
                </div>
              ` : ''}
            </div>
            
            <!-- RESUMO EXECUTIVO -->
            <div class="section">
              <h2 class="section-title">📈 Resumo Executivo</h2>
              <div class="highlight">
                <h4>Principais Indicadores do Período</h4>
                <p><strong>Volume total:</strong> ${dadosProcessados.totalSenhas} senhas processadas</p>
                <p><strong>Taxa de conclusão:</strong> ${dadosProcessados.totalSenhas > 0 ? (((dadosProcessados.statusCount['FINALIZADA'] || 0) / dadosProcessados.totalSenhas) * 100).toFixed(1) : '0'}%</p>
                <p><strong>Taxa de cancelamento:</strong> ${dadosProcessados.totalSenhas > 0 ? (((dadosProcessados.statusCount['CANCELADA'] || 0) / dadosProcessados.totalSenhas) * 100).toFixed(1) : '0'}%</p>
                                 <p><strong>Atendimentos preferenciais:</strong> ${dadosProcessados.totalSenhas > 0 ? (((dadosProcessados.tipoCount['Preferencial'] || 0) / dadosProcessados.totalSenhas) * 100).toFixed(1) : '0'}% do total</p>
                 <p><strong>Serviços únicos:</strong> ${Object.keys(dadosProcessados.servicoCount).filter(s => s !== 'N/A').length} tipos diferentes</p>
                 <p><strong>Serviço mais solicitado:</strong> ${Object.entries(dadosProcessados.servicoCount).filter(([s]) => s !== 'N/A').sort(([,a], [,b]) => b - a)[0]?.[0] || 'N/A'}</p>
              </div>
            </div>
            
            <!-- RODAPÉ -->
            <div class="footer">
              <img src="${logoImage}" alt="Logo" />
              <p>Sistema de Gerenciamento de Senhas - Relatório de Senhas Detalhadas</p>
              <p>© ${new Date().getFullYear()} - Todos os direitos reservados</p>
              <p>Gerado em ${dayjs().format('DD/MM/YYYY HH:mm:ss')}</p>
            </div>
          </body>
        </html>
      `;

      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Aguarda o carregamento e imprime
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
        
        if (typeof onSuccess === 'function') {
          onSuccess('Relatório de Senhas Detalhadas gerado com sucesso! A impressão foi iniciada automaticamente.');
        }
      };
      
    } catch (error) {
      console.error('Erro no método simplificado:', error);
      
      if (typeof onError === 'function') {
        onError('Não foi possível gerar o relatório: ' + error.message);
             }
     }
   }
 }

// Exportar a classe e uma instância
export { PDFSenhasDetalhadas };
export default new PDFSenhasDetalhadas(); 